import { useEffect } from "react";
import { motion } from "framer-motion";
import { Lock, MessageCircle, CheckCircle2 } from "lucide-react";
import LeadCaptureForm from "@/components/funnel/LeadCaptureForm";
import { HE } from "@/lib/i18n";
import { t } from "@/lib/strings";

export const LockGateModal = ({ domain, issues, lockedCounts, analysisId, campaign, onUnlocked }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const bullets = HE
    ? [
        "פירוט הציון המדויק שלכם בכל 6 הפלטפורמות",
        `${lockedCounts?.competitors ?? 4} מתחרים שה-AI ממליץ עליהם במקומכם`,
        "מפת תיקונים צעד-אחר-צעד — מה מתקנים קודם",
      ]
    : [
        `Your exact score breakdown on all ${6} AI platforms`,
        `${lockedCounts?.competitors ?? 4} competitors AI recommends instead of you`,
        "Step-by-step fix roadmap — what to fix first",
      ];

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center sm:p-6" data-testid="lock-gate-modal">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute inset-0 bg-obsidian/70 backdrop-blur-[6px]"
      />
      <motion.div
        initial={{ opacity: 0, y: 60, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 280, damping: 26 }}
        className="relative w-full sm:max-w-lg bg-white border-2 border-obsidian sm:shadow-[12px_12px_0px_0px_rgba(255,69,0,1)] max-h-[94vh] overflow-y-auto"
      >
        {/* alert strip */}
        <div className="bg-conversion text-white px-5 py-2.5 flex items-center justify-between sticky top-0 z-10">
          <span className="flex items-center gap-2 font-data text-[11px] font-bold uppercase tracking-[0.2em]">
            <motion.span animate={{ rotate: [0, -12, 12, -8, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8, repeatDelay: 1.2 }}>
              <Lock className="h-4 w-4" />
            </motion.span>
            {HE ? t("lock.locked") : "Results locked"}
          </span>
          <span className="font-data text-[10px] uppercase tracking-widest opacity-90">{HE ? t("lock.oneStep") : "1 step to unlock"}</span>
        </div>

        <div className="p-6 sm:p-8">
          {HE ? (
            <>
              <h3 className="font-display text-3xl sm:text-4xl leading-[1.02] text-obsidian">
                הדוח המלא שלכם <span className="text-conversion">מוכן.</span>
              </h3>
              <p className="mt-3 text-[15px] text-neutral-700 leading-relaxed">
                סרקנו את <span className="font-data font-bold text-obsidian" dir="ltr">{domain}</span> על פני 6 פלטפורמות AI ומצאנו{" "}
                <span className="font-bold text-conversion" data-testid="lock-issues-count">{issues} בעיות קריטיות</span>{" "}
                <strong>שעולות לכם בלקוחות</strong> — כל יום.
              </p>
            </>
          ) : (
            <>
              <h3 className="font-display text-3xl sm:text-4xl leading-[1.02] text-obsidian">
                Your full report is <span className="text-conversion">ready.</span>
              </h3>
              <p className="mt-3 text-[15px] text-neutral-700 leading-relaxed">
                We scanned <span className="font-data font-bold text-obsidian">{domain}</span> across 6 AI platforms and found{" "}
                <span className="font-bold text-conversion" data-testid="lock-issues-count">{issues} critical issues</span>{" "}
                <strong>costing you customers</strong> every day.
              </p>
            </>
          )}

          <ul className="mt-4 space-y-2">
            {bullets.map((it) => (
              <li key={it} className="flex items-start gap-2.5 text-sm text-obsidian">
                <CheckCircle2 className="h-5 w-5 text-data-positive shrink-0 mt-0.5" />
                <span className="leading-snug">{it}</span>
              </li>
            ))}
          </ul>

          {/* whatsapp promise */}
          <div className="mt-5 border-2 border-[#25D366] bg-[#25D366]/[0.08] p-4 flex gap-3">
            <div className="bg-[#25D366] p-2 h-fit shrink-0">
              <MessageCircle className="h-5 w-5 text-white" />
            </div>
            {HE ? (
              <p className="text-sm text-obsidian leading-snug">
                הדוח המלא — <span className="font-bold">ישר לוואטסאפ שלכם</span>. לוקח 10 שניות.{" "}
                <span className="font-bold text-conversion">100% חינם.</span>
              </p>
            ) : (
              <p className="text-sm text-obsidian leading-snug">
                Full report — <span className="font-bold">straight to your WhatsApp</span>. Takes 10 seconds.{" "}
                <span className="font-bold text-conversion">100% free.</span>
              </p>
            )}
          </div>

          <div className="mt-5">
            <LeadCaptureForm
              analysisId={analysisId}
              domain={domain}
              campaign={campaign}
              onUnlocked={onUnlocked}
              variant="light"
              ctaText={HE ? t("lock.cta") : "Unlock & Send to My WhatsApp"}
            />
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LockGateModal;
